import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Grid from '@material-ui/core/Grid'
import Loader from 'components/general/Loader'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import { getMoments } from 'config/helpers/Helpers'
import componentStyles from 'assets/theme/views/admin/alternative-dashboard.js'
import componentStylesCardDeck from 'assets/theme/components/cards/card-deck.js'
import moment from 'moment-timezone'
import { eeProductOptions } from 'config/constants/Constants'
import PeriodSelect from 'components/general/PeriodSelect'
import SolarTotals from 'components/admin/Energy/SolarTotals'
import EETypePie from 'components/admin/Energy/EETypePie'
import EEProductsTable from 'components/admin/Energy/EEProductsTable'

const useStyles = makeStyles(componentStyles)
const useStylesCardDeck = makeStyles(componentStylesCardDeck)

function EnergyEfficiency() {
    const classes = { ...useStyles(), ...useStylesCardDeck() }
    const theme = useTheme()
    const api = new EnergyAPI()

    const [leads, setLeads] = useState([])
    const [deliveries, setDeliveries] = useState([])
    const [productBreakdown, setProductBreakdown] = useState([])
    const [period, setPeriod] = useState('thisWeek')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [product, setProduct] = useState('all')
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadLeads()
    }, [])

    function computeProductBreakdown(leads, deliveries) {
        return eeProductOptions.map((p) => {
            const productLeads = leads.filter(l => l.product === p.value)
            const rejected = productLeads.filter(l => l.companyId === null && !l.isValid)
            const unallocated = productLeads.filter(l => l.companyId === null && l.isValid)
            const sold = productLeads.filter(l => l.companyId !== null)

            // Revenue (Delivery Price): sum of each sold lead's company/product
            // current delivery leadPrice. Reflects what leads of this type are
            // currently being billed at, using today's active delivery rates.
            const revenueDelivery = sold.reduce((sum, l) => {
                const delivery = deliveries.find(d => d.companyId === l.companyId && d.product === l.product)
                return sum + (parseFloat(delivery?.leadPrice) || 0)
            }, 0)

            // Revenue (Sold Price): sum of lead.leadPrice — the price persisted
            // on the lead at the moment it was allocated. This is the actual
            // billed price including any replacement/€0 overrides and any
            // historical price changes.
            const soldWithPrice = sold.filter(l => l.leadPrice != null && l.leadPrice !== '')
            const soldMissingPrice = sold.length - soldWithPrice.length
            const revenueSold = soldWithPrice.reduce(
                (sum, l) => sum + (parseFloat(l.leadPrice) || 0),
                0
            )

            return {
                product: p.label,
                generated: productLeads.length,
                rejected: rejected.length,
                unallocated: unallocated.length,
                sold: sold.length,
                soldMissingPrice,
                revenueSold,
                revenueDelivery
            }
        })
    }

    async function loadLeads() {
        setLoading(true)
        try {
            let dates = getMoments('thisWeek')

            let leads = []
            for (let p of eeProductOptions) {
                let productLeads = await api.getEeLeadsPeriod(p.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                leads = leads.concat(productLeads)
            }
            leads.sort((a, b) => b.createdAt - a.createdAt)

            const allDeliveries = await api.getEnergyDeliveries()
            console.log('deliveries: ', allDeliveries)

            setLeads(leads)
            setDeliveries(allDeliveries)
            setProductBreakdown(computeProductBreakdown(leads, allDeliveries))
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    async function updatePeriod(period, fromDate=null, toDate=null) {
        setPeriod(period)
        setLoading(true)
        let dates = period === 'custom' ? {
            fromDate: moment(fromDate).startOf('day'),
            toDate: moment(toDate).endOf('day')
        } : getMoments(period)

        try {
            let leads = []
            for (let p of eeProductOptions) {
                let productLeads = await api.getEeLeadsPeriod(p.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                leads = leads.concat(productLeads)
            }
            leads.sort((a, b) => {
                return b.createdAt - a.createdAt
            })

            setLeads(leads)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setProductBreakdown(computeProductBreakdown(leads, deliveries))
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    async function updateProduct(selectedProduct) {
        setLoading(true) 
        let dates = getMoments(period)
        try {
            let leads = []
            if (selectedProduct === 'all') {
                for (let p of eeProductOptions) {
                    let productLeads = await api.getEeLeadsPeriod(p.value, dates.fromDate.valueOf(), dates.toDate.valueOf())
                    leads = leads.concat(productLeads)
                }
            }
            else {
                let productLeads = await api.getEeLeadsPeriod(selectedProduct, dates.fromDate.valueOf(), dates.toDate.valueOf())
                leads = productLeads
            }
            leads.sort((a, b) => {
                return b.createdAt - a.createdAt
            })

            setLeads(leads)
            setFromDate(dates.fromDate)
            setToDate(dates.toDate)
            setProduct(selectedProduct)
            setProductBreakdown(computeProductBreakdown(leads, deliveries))
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    return (
        <>
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                classes={{ root: classes.containerRoot }}
            >
                {!loading ? (
                    <Grid container>
                        <Grid item xs={12}>
                            <Grid
                                container
                                component={Box}
                                alignItems='center'
                                justifyContent='flex-start'
                                style={{marginBottom: '2rem'}}
                                direction='row'
                            >
                                <Grid item xs={12} md={2}>
                                    <PeriodSelect
                                        period={period}
                                        updatePeriod={(period, fromDate, toDate) => updatePeriod(period, fromDate, toDate)}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined'>
                                            <Select
                                                multiple={false}
                                                defaultValue={null}
                                                value={product}
                                                onChange={(e) => updateProduct(e.target.value)}
                                            >
                                                <MenuItem value={'all'}>All</MenuItem>
                                                {eeProductOptions.map((item) => {
                                                    return (<MenuItem value={item.value}>{item.label}</MenuItem>)
                                                })}
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                    <Box
                                        fontSize='12px'
                                        color={theme.palette.gray[600]}
                                        marginTop='0.5rem'
                                        textAlign='left'
                                    >
                                        <Box component='small' fontSize='100%'>
                                            Product
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <SolarTotals
                                leads={leads}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <EEProductsTable
                                productBreakdown={productBreakdown}
                                fromDate={fromDate}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <EETypePie
                                leads={leads}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    );
}

export default EnergyEfficiency